/**
 * Care coverage for a residential day: what the roster will deliver, against
 * what the facility is required to deliver.
 *
 * WHY THIS EXISTS. Residential aged care is staffed to a regulated minimum, not
 * to a set of client promises. From 1 April 2026 care funding is linked to
 * delivered care minutes, and providers must file an externally audited Care
 * Minutes Performance Statement. The roster is where that number is decided,
 * and it did not show it — so a coordinator could staff every shift and still
 * be non-compliant, and only learn of it from a quarterly report, months after
 * the day they could have changed.
 *
 * TWO THINGS THIS DELIBERATELY REFUSES TO DO.
 *
 * It will not invent a target. The sector average is ~215 total and ~44 RN
 * minutes per resident per day, but a facility's obligation is casemix-adjusted
 * and is its own number. Medea Park — the live residential provider — has NO
 * `care_minute_targets` row. Comparing them against a sector average and
 * printing a green tick would be a compliance claim built on a guess. Absent a
 * target this returns `NO_TARGET`, which is itself the finding: compliance
 * cannot be assessed until someone configures it.
 *
 * It will not call rostered minutes "delivered". A rostered shift is an
 * intention. Sick leave, late starts and early departures all erode it, which
 * is why the practice guidance is to roster ABOVE target rather than to it.
 * Everything here is named `rostered` for that reason, and the buffer band
 * exists so a roster that exactly meets target is shown as at risk rather than
 * as passing.
 */

/** Minutes are rostered, never delivered. The distinction is the whole point. */
export type CareCoverageInput = {
  /** Paid minutes that count toward total care minutes, already attributed. */
  readonly rosteredCareMinutes: number;
  /** Subset of the above that counts toward the RN component. */
  readonly rosteredRnMinutes: number;
  /** Occupied beds for the day. Zero means the ratio is undefined, not zero. */
  readonly occupiedBeds: number;
  /**
   * The facility's own casemix-adjusted obligation, per resident per day.
   * Null when no target is configured — do not substitute a sector average.
   */
  readonly targetTotalMinutesPerResident: number | null;
  readonly targetRnMinutesPerResident: number | null;
  /**
   * Fraction above target treated as the safe operating band. Rostering to
   * exactly the target reliably under-delivers once absence is accounted for.
   */
  readonly bufferFraction?: number;
};

export type CareCoverageStatus =
  /** No configured target. Compliance cannot be assessed — not a pass. */
  | 'NO_TARGET'
  /** No occupied beds, so a per-resident ratio has no meaning. */
  | 'NO_OCCUPANCY'
  /** Below the required minimum. */
  | 'UNDER'
  /** At or above the minimum, but inside the buffer — likely to under-deliver. */
  | 'AT_RISK'
  /** At or above the minimum plus buffer. */
  | 'MET';

export type CareCoverageMeasure = {
  readonly rosteredPerResident: number | null;
  readonly targetPerResident: number | null;
  /** Positive means above target. Null when there is nothing to compare. */
  readonly variancePerResident: number | null;
  readonly status: CareCoverageStatus;
};

export type CareCoverageSummary = {
  readonly total: CareCoverageMeasure;
  readonly rn: CareCoverageMeasure;
  /**
   * Worst of the two. RN and total are graded separately because the RN
   * component can fail while the total passes — a facility can look fully
   * staffed and still breach the nursing obligation.
   */
  readonly overall: CareCoverageStatus;
  readonly occupiedBeds: number;
};

const DEFAULT_BUFFER_FRACTION = 0.1;

function measure(
  rosteredMinutes: number,
  targetPerResident: number | null,
  occupiedBeds: number,
  bufferFraction: number,
): CareCoverageMeasure {
  if (occupiedBeds <= 0) {
    return {
      rosteredPerResident: null,
      targetPerResident,
      variancePerResident: null,
      status: 'NO_OCCUPANCY',
    };
  }
  const rosteredPerResident = rosteredMinutes / occupiedBeds;
  if (targetPerResident == null) {
    // Rostered minutes are still reported — they are a fact. What cannot be
    // reported is whether they are enough.
    return {
      rosteredPerResident,
      targetPerResident: null,
      variancePerResident: null,
      status: 'NO_TARGET',
    };
  }
  const variancePerResident = rosteredPerResident - targetPerResident;
  const bufferThreshold = targetPerResident * (1 + bufferFraction);
  const status: CareCoverageStatus =
    rosteredPerResident < targetPerResident
      ? 'UNDER'
      : rosteredPerResident < bufferThreshold
        ? 'AT_RISK'
        : 'MET';
  return { rosteredPerResident, targetPerResident, variancePerResident, status };
}

/** Worst status wins. Order is deliberate: an unassessable day is not a pass. */
const STATUS_SEVERITY: Record<CareCoverageStatus, number> = {
  UNDER: 4,
  NO_TARGET: 3,
  NO_OCCUPANCY: 2,
  AT_RISK: 1,
  MET: 0,
};

export function buildCareCoverageSummary(input: CareCoverageInput): CareCoverageSummary {
  const buffer = input.bufferFraction ?? DEFAULT_BUFFER_FRACTION;
  const total = measure(
    input.rosteredCareMinutes,
    input.targetTotalMinutesPerResident,
    input.occupiedBeds,
    buffer,
  );
  const rn = measure(
    input.rosteredRnMinutes,
    input.targetRnMinutesPerResident,
    input.occupiedBeds,
    buffer,
  );
  const overall =
    STATUS_SEVERITY[total.status] >= STATUS_SEVERITY[rn.status] ? total.status : rn.status;
  return { total, rn, overall, occupiedBeds: input.occupiedBeds };
}

/**
 * Hours in a local day with no registered nurse in the caller's cover set.
 *
 * An RN must be on site 24/7. Callers build the evidence-aware cover set: a
 * named internal RN assignment or coherent provider-backed agency RN roster
 * commitment. An ordinary unfilled RN shift is excluded. The helper only
 * measures the timeline; it does not promote planned cover to actual attendance.
 *
 * Intervals are half-open [start, end): a shift ending at 15:00 does not cover
 * the 15:00 hour, so a handover gap is reported rather than papered over.
 */
export function rnCoverageGapHours(input: {
  readonly dayStartMs: number;
  /** Exclusive. */
  readonly dayEndMs: number;
  readonly assignedRnIntervals: ReadonlyArray<{ readonly startMs: number; readonly endMs: number }>;
}): number[] {
  const gaps: number[] = [];
  const HOUR = 3_600_000;
  for (let hourStart = input.dayStartMs; hourStart < input.dayEndMs; hourStart += HOUR) {
    const covered = input.assignedRnIntervals.some(
      (interval) => interval.startMs <= hourStart && interval.endMs > hourStart,
    );
    if (!covered) gaps.push(hourStart);
  }
  return gaps;
}

/** Human-readable status, for a surface that must not overstate what it knows. */
export function careCoverageStatusLabel(status: CareCoverageStatus): string {
  switch (status) {
    case 'NO_TARGET':
      return 'No care-minute target configured — compliance cannot be assessed';
    case 'NO_OCCUPANCY':
      return 'No occupied beds recorded for this day';
    case 'UNDER':
      return 'Below the required minimum';
    case 'AT_RISK':
      return 'Meets the minimum with no margin for absence';
    case 'MET':
      return 'Meets the minimum with margin';
  }
}
